import { Card, CardContent, Badge, Button, cn, CLIENT_LOCALE } from '@yeshuman/sdk'
import { Activity as ActivityType } from '../hooks/use-activities'
import { Edit, Trash2, Activity, Moon, Heart } from 'lucide-react'

interface ActivityCardProps {
  activity: ActivityType
  onEdit?: (activity: ActivityType) => void
  onDelete?: (activityId: string) => void
  isHighlighted?: boolean
}

export function ActivityCard({ activity, onEdit, onDelete, isHighlighted }: ActivityCardProps) {
  const getActivityIcon = () => {
    switch (activity.type) {
      case 'exercise':
        return <Activity className="h-4 w-4" />
      case 'sleep':
        return <Moon className="h-4 w-4" />
      case 'wellness':
        return <Heart className="h-4 w-4" />
      default:
        return <Activity className="h-4 w-4" />
    }
  }

  const getActivityBadgeColor = () => {
    switch (activity.type) {
      case 'exercise':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
      case 'sleep':
        return 'bg-purple-500/10 text-purple-700 dark:text-purple-400'
      case 'wellness':
        return 'bg-green-500/10 text-green-700 dark:text-green-400'
      default:
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
    }
  }

  const formatDuration = (durationStr?: string) => {
    if (!durationStr) return null
    
    // Parse ISO duration format (PT30M, PT1H30M, PT8H)
    const match = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
    if (!match) return durationStr
    
    const hours = parseInt(match[1] || '0')
    const minutes = parseInt(match[2] || '0')
    const seconds = parseInt(match[3] || '0')
    
    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (seconds > 0) parts.push(`${seconds}s`)
    
    return parts.length > 0 ? parts.join(' ') : null
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString(CLIENT_LOCALE, { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }

  const formatDateTime = (dateTimeStr?: string) => {
    if (!dateTimeStr) return null
    const date = new Date(dateTimeStr)
    return date.toLocaleString(CLIENT_LOCALE, { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  const getActivityDetails = () => {
    switch (activity.type) {
      case 'exercise':
        return {
          title: activity.exercise_type || 'Exercise',
          duration: formatDuration(activity.duration),
        }
      case 'sleep':
        return {
          title: 'Sleep',
          duration: formatDuration(activity.duration),
          startTime: formatDateTime(activity.sleep_start_time),
        }
      case 'wellness':
        return {
          title: activity.wellness_type || 'Wellness',
          duration: formatDuration(activity.duration),
        }
      default:
        return { title: 'Activity' }
    }
  }

  const details = getActivityDetails()

  return (
    <Card
      className={cn(
        "transition-all duration-300 hover:shadow-md",
        isHighlighted && "ring-2 ring-primary ring-offset-2 animate-pulse"
      )}
    >
      <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Badge className={cn("flex items-center gap-1", getActivityBadgeColor())}>
                {getActivityIcon()}
                <span className="capitalize">{activity.type}</span>
              </Badge>
              <span className="text-sm text-muted-foreground">{formatDate(activity.date)}</span>
            </div>
            
            <div>
              <h3 className="font-semibold">{details.title}</h3>
              {details.startTime && (
                <p className="text-sm text-muted-foreground">Started: {details.startTime}</p>
              )}
              {details.duration && (
                <p className="text-sm text-muted-foreground">Duration: {details.duration}</p>
              )}
              {activity.notes && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{activity.notes}</p>
              )}
            </div>
          </div>
          
          {(onEdit || onDelete) && (
            <div className="flex gap-1 ml-4">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={() => onEdit(activity)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={() => onDelete(activity.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

